import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import { Card, CardContent, Typography, Box } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import {
  chartColors,
  barDefaults,
  mergeOptions,
  withOpacity
} from '../../theme/chartTheme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function KwTonComparison({ data, title = 'kW/Ton Before vs After Treatment' }) {
  const chartData = useMemo(() => {
    // Group by equipment type and calculate averages
    const byType = {}

    data.forEach(study => {
      if (!study.equipment_type || !study.kw_per_ton_before || !study.kw_per_ton_after) return

      if (!byType[study.equipment_type]) {
        byType[study.equipment_type] = {
          beforeSum: 0,
          afterSum: 0,
          count: 0
        }
      }

      byType[study.equipment_type].beforeSum += study.kw_per_ton_before
      byType[study.equipment_type].afterSum += study.kw_per_ton_after
      byType[study.equipment_type].count++
    })

    const labels = Object.keys(byType).sort()
    const beforeData = labels.map(type =>
      byType[type].count > 0 ? byType[type].beforeSum / byType[type].count : 0
    )
    const afterData = labels.map(type =>
      byType[type].count > 0 ? byType[type].afterSum / byType[type].count : 0
    )

    return {
      labels,
      datasets: [
        {
          label: 'Before Treatment (kW/Ton)',
          data: beforeData,
          backgroundColor: withOpacity(chartColors.error, 0.75),
          hoverBackgroundColor: chartColors.error,
          borderColor: chartColors.error,
          borderWidth: 0,
          borderRadius: barDefaults.borderRadius
        },
        {
          label: 'After Treatment (kW/Ton)',
          data: afterData,
          backgroundColor: withOpacity(chartColors.primary.main, 0.85),
          hoverBackgroundColor: chartColors.primary.main,
          borderColor: chartColors.primary.dark,
          borderWidth: 0,
          borderRadius: barDefaults.borderRadius
        }
      ]
    }
  }, [data])

  const options = mergeOptions({
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11,
            weight: 500
          },
          color: chartColors.textSecondary
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.raw.toFixed(3)} kW/Ton`
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'kW per Ton',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          color: chartColors.grid
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 11
          },
          color: chartColors.textSecondary
        }
      },
      x: {
        title: {
          display: true,
          text: 'Equipment Type',
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 12,
            weight: 600
          },
          color: chartColors.textSecondary
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: '"Plus Jakarta Sans", sans-serif',
            size: 10,
            weight: 500
          },
          color: chartColors.textSecondary
        }
      }
    }
  })

  // Calculate overall improvement
  const improvement = useMemo(() => {
    const withData = data.filter(d => d.kw_per_ton_before && d.kw_per_ton_after)
    if (withData.length === 0) return 0

    const avgBefore = withData.reduce((s, d) => s + d.kw_per_ton_before, 0) / withData.length
    const avgAfter = withData.reduce((s, d) => s + d.kw_per_ton_after, 0) / withData.length

    return ((avgBefore - avgAfter) / avgBefore * 100).toFixed(1)
  }, [data])

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: chartColors.success,
              fontWeight: 700,
              backgroundColor: withOpacity(chartColors.success, 0.1),
              px: 1.5,
              py: 0.5,
              borderRadius: 1
            }}
          >
            Avg. Reduction: {improvement}%
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Lower kW/Ton = More Efficient (less energy per ton of cooling)
        </Typography>
        <Box sx={{ height: 280 }}>
          <Bar data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default KwTonComparison
